// Subscription errors that a token refresh + rebuild can usually fix. Any
// other reason (network_error, unknown, http_4xx, …) is left alone.
export type RecoveryReason = 'expired_token' | 'unauthorized' | 'connection_closed';

const RECOVERABLE_REASONS: ReadonlySet<string> = new Set<RecoveryReason>([
    'expired_token',
    'unauthorized',
    'connection_closed',
]);

export const isRecoverableReason = (reason: string): boolean =>
    RECOVERABLE_REASONS.has(reason);

export interface RecoveryOutcome {
    outcome: 'completed' | 'failed';
    cycleId: string;
    reason: string;
    attempts: number;
    durationMs: number;
}

export interface RecoveryHandlers {
    onRecoveryOutcome?: (outcome: RecoveryOutcome) => void;
    // Runs the actual recovery: refresh the token, refetch lists, rebuild
    // subscriptions. Rejecting (or throwing) triggers a backoff retry.
    recover: () => Promise<void> | void;
    // Called once retries are exhausted — the refresh token itself is dead, so
    // this is a genuine session end rather than a transient expiry.
    onRecoveryStopped?: () => void;
}

export interface SubscriptionRecoveryOptions {
    debounceMs?: number;
    backoffScheduleMs?: readonly number[];
}

export interface SubscriptionRecoveryCoordinator {
    // Report a classified subscription error reason. Recoverable errors start (or join) a single
    // debounced recovery cycle; everything else is ignored.
    notify: (reason: string) => void;
    // Register the recovery handlers. Returns an unsubscribe function.
    subscribe: (handlers: RecoveryHandlers) => () => void;
    // Cancel any pending/in-flight cycle and clear the stopped state (e.g. on sign-out).
    cancel: () => void;
    hasGivenUp: () => boolean;
}

const DEFAULT_DEBOUNCE_MS = 300;
// 300 ms → 1 s → 3 s → 5 s → 10 s. When the schedule is exhausted recovery
// stops and the session is treated as dead.
const DEFAULT_BACKOFF_SCHEDULE_MS: readonly number[] = [300, 1000, 3000, 5000, 10000];

type State = 'idle' | 'active' | 'stopped';

export const createSubscriptionRecoveryCoordinator = (
    options: SubscriptionRecoveryOptions = {},
): SubscriptionRecoveryCoordinator => {
    const debounceMs = options.debounceMs ?? DEFAULT_DEBOUNCE_MS;
    const backoffScheduleMs = options.backoffScheduleMs ?? DEFAULT_BACKOFF_SCHEDULE_MS;

    let callbacks: RecoveryHandlers | undefined;
    let state: State = 'idle';
    let attempt = 0;
    let cycle: { id: string; reason: string; startedAt: number } | undefined;

    const reportOutcome = (outcome: RecoveryOutcome['outcome']) => {
        if (!cycle) return;
        try {
            callbacks?.onRecoveryOutcome?.({
                outcome,
                cycleId: cycle.id,
                reason: cycle.reason,
                attempts: attempt + 1,
                durationMs: Date.now() - cycle.startedAt,
            });
        } catch {
            // Diagnostics must never change retry or sign-out behavior.
        }
    };
    let timer: ReturnType<typeof setTimeout> | undefined;

    const clearTimer = () => {
        if (timer !== undefined) {
            clearTimeout(timer);
            timer = undefined;
        }
    };

    const goIdle = () => {
        state = 'idle';
        attempt = 0;
        cycle = undefined;
    };

    const goStopped = () => {
        clearTimer();
        state = 'stopped';
        reportOutcome('failed');
        cycle = undefined;
        attempt = 0;
        callbacks?.onRecoveryStopped?.();
    };

    const scheduleAttempt = (delay: number) => {
        clearTimer();
        timer = setTimeout(runAttempt, delay);
    };

    const runAttempt = () => {
        timer = undefined;

        if (!callbacks) {
            // Nothing wired yet (module does nothing until AppShell subscribes).
            goIdle();
            return;
        }

        const activeCallbacks = callbacks;
        const activeCycle = cycle;
        Promise.resolve()
            .then(() => {
                if (cycle !== activeCycle || state !== 'active') return;
                return activeCallbacks.recover();
            })
            .then(() => {
                if (state === 'active' && cycle === activeCycle) {
                    reportOutcome('completed');
                    goIdle();
                }
            })
            .catch(() => {
                if (state !== 'active' || cycle !== activeCycle) return;

                if (attempt >= backoffScheduleMs.length) {
                    goStopped();
                    return;
                }

                const delay = backoffScheduleMs[attempt];
                attempt += 1;
                scheduleAttempt(delay);
            });
    };

    const notify = (reason: string) => {
        // Stopped: dead session. Active: a recovery cycle is already running.
        if (state !== 'idle') return;
        if (!isRecoverableReason(reason)) return;

        state = 'active';
        cycle = { id: crypto.randomUUID(), reason, startedAt: Date.now() };
        scheduleAttempt(debounceMs);
    };

    const subscribe = (next: RecoveryHandlers) => {
        callbacks = next;
        return () => {
            if (callbacks === next) callbacks = undefined;
        };
    };

    const cancel = () => {
        clearTimer();
        goIdle();
    };

    return {
        notify,
        subscribe,
        cancel,
        hasGivenUp: () => state === 'stopped',
    };
};

// App-wide singleton. Wired up in AppShell; does nothing until then.
export const subscriptionRecovery = createSubscriptionRecoveryCoordinator();
